/**
 * 任务 ViewModel（UI 展示层）
 *
 * 设计原则：
 * 1. 基于领域模型，添加 UI 派生字段
 * 2. 包含格式化、计算、UI 状态等前端特有逻辑
 * 3. 不修改领域模型，通过 selector/computed 生成
 *
 * @version 4.0.0
 * @author Frontend Team
 * @since 2026-01-23
 */

import type { Task, TaskItem } from './task.domain'
import {
  isTaskCompleted,
  canCancelTask,
  canRetryTask,
  canDownloadTask,
  getPrimaryAudioUrl,
  calculateTotalDuration,
  calculateTotalFileSize
} from './task.domain'
import { formatters } from '@/utils/formatters'

// ============= ViewModel 接口 =============

/**
 * Task ViewModel - UI 展示用
 *
 * 包含所有 UI 需要的派生字段
 */
export interface TaskViewModel extends Task {
  // UI 派生字段
  displayTitle: string          // 显示标题（带默认值）
  displayStatus: string         // 本地化状态文本
  statusColor: string           // 状态颜色
  statusIcon: string            // 状态图标

  // 能力判断
  canCancel: boolean            // 是否可以取消
  canRetry: boolean             // 是否可以重试
  canDownload: boolean          // 是否可以下载
  canPlay: boolean              // 是否可以播放

  // 音频信息（聚合）
  primaryAudioUrl?: string      // 主音频 URL
  totalDuration: number         // 总时长（秒）
  totalFileSize: number         // 总文件大小（字节）
  formattedDuration: string     // 格式化时长
  formattedFileSize: string     // 格式化文件大小

  // 时间信息（格式化）
  formattedCreatedAt: string    // 格式化创建时间
  formattedCompletedAt?: string // 格式化完成时间
  elapsedTime?: string          // 已用时间

  // 批量任务特有
  progressPercent?: number      // 进度百分比
  completionRate?: string       // 完成率文本
}

/**
 * TaskItem ViewModel - UI 展示用
 */
export interface TaskItemViewModel extends TaskItem {
  // UI 派生字段
  displayStatus: string         // 本地化状态文本
  statusColor: string           // 状态颜色

  // 音频信息（从 segment 提取）
  audioUrl?: string
  durationSec?: number
  fileSize?: number
  formattedDuration?: string
  formattedFileSize?: string

  // 文件信息（从 file 提取）
  fileName?: string
  fileContent?: string
}

// ============= ViewModel 工厂函数 =============

/**
 * 创建 Task ViewModel
 */
export function createTaskViewModel(task: Task): TaskViewModel {
  const totalDuration = calculateTotalDuration(task)
  const totalFileSize = calculateTotalFileSize(task)

  return {
    ...task,

    // 显示标题
    displayTitle: task.title || getDefaultTitle(task),

    // 状态相关
    displayStatus: getStatusText(task.status),
    statusColor: getStatusColor(task.status),
    statusIcon: getStatusIcon(task.status),

    // 能力判断
    canCancel: canCancelTask(task),
    canRetry: canRetryTask(task),
    canDownload: canDownloadTask(task),
    canPlay: isTaskCompleted(task) && task.type === 'single',

    // 音频信息
    primaryAudioUrl: getPrimaryAudioUrl(task),
    totalDuration,
    totalFileSize,
    formattedDuration: formatters.duration.formatDuration(totalDuration),
    formattedFileSize: formatters.fileSize.formatFileSize(totalFileSize),

    // 时间信息
    formattedCreatedAt: formatters.date.getRelativeTime(task.createdAt),
    formattedCompletedAt: task.completedAt
      ? formatters.date.getRelativeTime(task.completedAt)
      : undefined,
    elapsedTime: calculateElapsedTime(task),

    // 批量任务特有
    progressPercent: task.totalItems
      ? Math.round((task.progress || 0))
      : undefined,
    completionRate: task.totalItems
      ? `${task.completedItems || 0}/${task.totalItems}`
      : undefined
  }
}

/**
 * 创建 TaskItem ViewModel
 */
export function createTaskItemViewModel(item: TaskItem): TaskItemViewModel {
  const segment = item.segment
  const file = item.file

  return {
    ...item,

    // 状态相关
    displayStatus: getItemStatusText(item.status),
    statusColor: getItemStatusColor(item.status),

    // 音频信息（从 segment 提取）
    audioUrl: segment?.audioUrl,
    durationSec: segment?.durationSec,
    fileSize: segment?.fileSize,
    formattedDuration: segment?.durationSec
      ? formatters.duration.formatDuration(segment.durationSec)
      : undefined,
    formattedFileSize: segment?.fileSize
      ? formatters.fileSize.formatFileSize(segment.fileSize)
      : undefined,

    // 文件信息（从 file 提取）
    fileName: file?.originalName,
    fileContent: file?.parsedContent
  }
}

// ============= 辅助函数 =============

/**
 * 获取默认标题
 */
function getDefaultTitle(task: Task): string {
  if (task.type === 'single') {
    return '单次转换任务'
  } else if (task.type === 'batch') {
    return `批量转换任务 (${task.totalItems || 0} 个文件)`
  }
  return '未命名任务'
}

/**
 * 获取状态文本
 */
function getStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    'created': '已创建',
    'queued': '排队中',
    'processing': '转换中',
    'post_processing': '后处理中',
    'completed': '已完成',
    'processing_failed': '转换失败',
    'post_failed': '后处理失败',
    'cancelled': '已取消'
  }
  return statusMap[status] || status
}

/**
 * 获取状态颜色
 */
function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    'created': '#909399',
    'queued': '#409EFF',
    'processing': '#409EFF',
    'post_processing': '#409EFF',
    'completed': '#67C23A',
    'processing_failed': '#F56C6C',
    'post_failed': '#F56C6C',
    'cancelled': '#909399'
  }
  return colorMap[status] || '#909399'
}

/**
 * 获取状态图标
 */
function getStatusIcon(status: string): string {
  const iconMap: Record<string, string> = {
    'created': 'clock',
    'queued': 'timer',
    'processing': 'loading',
    'post_processing': 'loading',
    'completed': 'check-circle',
    'processing_failed': 'close-circle',
    'post_failed': 'close-circle',
    'cancelled': 'close'
  }
  return iconMap[status] || 'question'
}

/**
 * 获取任务项状态文本
 */
function getItemStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    'pending': '等待中',
    'running': '执行中',
    'completed': '成功',
    'failed': '失败'
  }
  return statusMap[status] || status
}

/**
 * 获取任务项状态颜色
 */
function getItemStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    'pending': '#909399',
    'running': '#409EFF',
    'completed': '#67C23A',
    'failed': '#F56C6C'
  }
  return colorMap[status] || '#909399'
}

/**
 * 计算已用时间
 */
function calculateElapsedTime(task: Task): string | undefined {
  if (!task.startedAt) return undefined

  const endTime = task.completedAt
    ? new Date(task.completedAt).getTime()
    : Date.now()

  const startTime = new Date(task.startedAt).getTime()
  const elapsed = Math.floor((endTime - startTime) / 1000)

  return formatters.duration.formatDuration(elapsed)
}
